import { Globe, Search, Megaphone, Share2, Headphones, BarChart3, ShoppingCart, MessageSquare, Mail, MessageCircle, PenLine, Linkedin, MousePointerClick, ThumbsUp, TrendingUp, Gamepad2, Wrench, Server, Database, Plug, Smartphone, Code2, LayoutTemplate, Store } from "lucide-react";

export const CONTACT = {
  email: "support@levaze.in",
  phone: "+91 8104713741",
  phoneLink: "tel:+918104713741",
  whatsapp: "https://wa.me/918104713741?text=Hi,%20I%20want%20to%20know%20more%20about%20your%20Services",
  addresses: [
    { label: "Noida Office", text: "L-2A, Block J-444, Pocket G, Sector 18, Noida, Uttar Pradesh 201301" },
    { label: "Mumbai Office", text: "402, A Wing, Asmita Vintage, Mira Road East, Maharashtra 401107" },
    { label: "Haridwar Office", text: "S-46 Shivalik Nagar, Haridwar, Uttarakhand 249403" },
  ],
};

export const SERVICES = [
  {
    icon: Globe,
    title: "Web Design",
    tagline: "Websites that build trust instantly",
    description: "Sleek, modern, responsive websites tailored to your brand — with clear calls to action that convert visitors into customers.",
    points: ["Responsive & mobile-first builds", "Conversion-focused layouts", "Brand-aligned visual identity"],
  },
  {
    icon: Search,
    title: "SEO",
    tagline: "Be found where it matters",
    description: "Technical audits, on-page optimisation and content strategies that push your brand to the top of search results.",
    points: ["Keyword & competitor research", "Technical SEO audits", "Local SEO for Indian markets"],
  },
  {
    icon: Megaphone,
    title: "Digital Marketing",
    tagline: "Campaigns that actually convert",
    description: "Smart online campaigns across Google and Meta, engineered for reach, leads and measurable ROI.",
    points: ["Google & Meta ad management", "Funnel design & retargeting", "Monthly performance reports"],
  },
  {
    icon: Share2,
    title: "Social Media",
    tagline: "Grow a community, not just followers",
    description: "Content calendars, creatives and engagement strategies that turn your social channels into growth engines.",
    points: ["Content strategy & creatives", "Community management", "Influencer collaborations"],
  },
  {
    icon: Headphones,
    title: "Support",
    tagline: "A partner, not just a vendor",
    description: "Ongoing maintenance, updates and a dedicated team that keeps your digital presence running flawlessly.",
    points: ["Priority WhatsApp support", "Website maintenance & updates", "Dedicated account manager"],
  },
  {
    icon: BarChart3,
    title: "Analytics",
    tagline: "Decisions backed by data",
    description: "Dashboards and insight reports that show exactly where your growth comes from — and where to invest next.",
    points: ["GA4 & conversion tracking", "Custom growth dashboards", "Quarterly strategy reviews"],
  },
];

// ─── Marketing Services (Services ▾) ───────────────────────────────
export const MARKETING_SERVICES = [
  {
    slug: "seo", icon: Search, title: "SEO",
    tagline: "Be found where it matters",
    description: "Rank higher, get discovered and turn organic search into a steady stream of qualified leads.",
    overview: "Our SEO practice combines deep technical audits, intent-driven keyword research and content that earns rankings. We focus on the searches your customers actually type — then make sure your brand owns that moment. From local map packs to national keywords, every move is measured against real business outcomes, not vanity metrics.",
    heroImage: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    included: ["Technical SEO audit & fixes", "Keyword & competitor research", "On-page & content optimisation", "Local SEO for Indian markets", "Monthly ranking & traffic reports"],
    result: "Avg. 3x organic traffic in 6 months",
  },
  {
    slug: "e-commerce-marketing", icon: ShoppingCart, title: "E-Commerce Marketing",
    tagline: "Turn browsers into buyers",
    description: "Full-funnel campaigns that drive traffic, recover carts and grow repeat revenue for online stores.",
    overview: "We build revenue engines for e-commerce brands — from product-feed ads and shopping campaigns to email flows and retargeting that win back abandoned carts. Every rupee of spend is tracked to sales so you always know your true return on ad spend.",
    heroImage: "https://images.unsplash.com/photo-1563013544-824ae1b704d3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    included: ["Shopping & product-feed ads", "Cart-abandonment recovery flows", "Retargeting across Google & Meta", "Conversion-rate optimisation", "ROAS-focused reporting"],
    result: "Higher ROAS, lower cart abandonment",
  },
  {
    slug: "sms-and-bulk-marketing", icon: MessageSquare, title: "SMS & Bulk Marketing",
    tagline: "Land directly in every pocket",
    description: "High-open-rate SMS campaigns that reach your entire audience instantly — offers, alerts and reminders.",
    overview: "SMS still delivers open rates email can only dream of. We plan, write and send compliant bulk campaigns — promotions, order updates and reminders — segmented so the right message reaches the right customer at the right time.",
    heroImage: "https://images.unsplash.com/photo-1603145733146-ae562a55031e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    included: ["Audience segmentation & lists", "Promotional & transactional SMS", "DLT-compliant sender setup", "Scheduled & triggered sends", "Delivery & click reporting"],
    result: "Up to 98% message open rates",
  },
  {
    slug: "email-marketing", icon: Mail, title: "Email Marketing",
    tagline: "Nurture leads into loyal customers",
    description: "Automated email journeys and campaigns that stay top-of-mind and quietly drive repeat sales.",
    overview: "We design beautiful, on-brand emails and the automation behind them — welcome series, nurture flows, newsletters and win-backs. Clean lists, sharp copy and constant testing keep your audience engaged and converting.",
    heroImage: "https://images.unsplash.com/photo-1586880244406-556ebe35f282?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    included: ["Automated welcome & nurture flows", "Newsletter design & copywriting", "List segmentation & hygiene", "A/B subject-line testing", "Open, click & revenue tracking"],
    result: "Consistent repeat-purchase revenue",
  },
  {
    slug: "whatsapp-marketing", icon: MessageCircle, title: "WhatsApp Marketing",
    tagline: "Conversations that convert",
    description: "Official WhatsApp campaigns and chat flows that reach customers where they already are.",
    overview: "With WhatsApp Business, we turn a messaging app into a sales channel — broadcast offers, automated replies, catalogue sharing and order confirmations. Personal, instant and trusted, it's the fastest way to move a lead to a decision.",
    heroImage: "https://images.unsplash.com/photo-1724862936518-ae7fcfc052c1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    included: ["WhatsApp Business API setup", "Broadcast & drip campaigns", "Automated chat flows & replies", "Catalogue & order integration", "Opt-in list building"],
    result: "Instant reach, high engagement",
  },
  {
    slug: "public-relations", icon: Megaphone, title: "Public Relations",
    tagline: "Build a reputation that sells",
    description: "Media coverage, press outreach and storytelling that put your brand in front of the right audience.",
    overview: "We craft the story, then get it told — press releases, journalist outreach, features and thought-leadership placements. Strong PR builds the trust that makes every other marketing rupee work harder.",
    heroImage: "https://images.unsplash.com/photo-1516280440614-37939bbacd81?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    included: ["Press-release writing & distribution", "Media & journalist outreach", "Feature & interview placements", "Brand-story development", "Crisis & reputation guidance"],
    result: "Earned coverage that builds trust",
  },
  {
    slug: "content-marketing", icon: PenLine, title: "Content Marketing",
    tagline: "Content that earns attention",
    description: "Blogs, guides and creatives that pull in your audience and gently guide them toward you.",
    overview: "Great content answers the questions your customers are already asking. We plan editorial calendars, write SEO-ready articles and produce creatives that position you as the obvious choice — building an audience that compounds over time.",
    heroImage: "https://images.unsplash.com/photo-1497032628192-86f99bcd76bc?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    included: ["Editorial strategy & calendar", "SEO blog & article writing", "Lead magnets & guides", "Creative & visual assets", "Performance-based topic planning"],
    result: "Compounding organic audience growth",
  },
  {
    slug: "linkedin-ads-marketing", icon: Linkedin, title: "LinkedIn Ads-Marketing",
    tagline: "Reach decision-makers directly",
    description: "Precision B2B campaigns targeting the exact titles, industries and companies you want to sell to.",
    overview: "For B2B brands, LinkedIn is where the buyers are. We build laser-targeted ad campaigns — sponsored content, lead-gen forms and retargeting — aimed at the roles and companies that fit your ideal customer profile.",
    heroImage: "https://images.unsplash.com/photo-1716638013730-11d8f264bfa3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    included: ["Ideal-customer-profile targeting", "Sponsored content & message ads", "Lead-gen form campaigns", "Account-based retargeting", "Cost-per-lead optimisation"],
    result: "Qualified B2B leads on autopilot",
  },
  {
    slug: "facebook-and-instagram-marketing", icon: Share2, title: "Facebook & Instagram Marketing",
    tagline: "Scroll-stopping campaigns",
    description: "Meta ad campaigns and content that grab attention, spark engagement and drive measurable sales.",
    overview: "From thumb-stopping creatives to finely tuned audience targeting, we run full Meta campaigns across Facebook and Instagram. Reels, stories, carousels and retargeting funnels — all optimised daily for the lowest cost per result.",
    heroImage: "https://images.unsplash.com/photo-1579869847557-1f67382cc158?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    included: ["Creative & reel production", "Audience research & targeting", "Full-funnel campaign setup", "Retargeting & lookalikes", "Daily optimisation & reporting"],
    result: "Lower cost per result, more sales",
  },
  {
    slug: "ppc-marketing", icon: MousePointerClick, title: "PPC Marketing",
    tagline: "Pay only for real results",
    description: "Google & Meta pay-per-click campaigns engineered for maximum leads at the lowest possible cost.",
    overview: "Every click should earn its keep. We structure search, display and performance-max campaigns with tight keyword control, strong landing pages and relentless optimisation — so your budget goes to clicks that actually convert.",
    heroImage: "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    included: ["Search & display campaign setup", "Keyword & bidding strategy", "Landing-page conversion tips", "Negative-keyword management", "Weekly performance optimisation"],
    result: "More leads at a lower cost-per-lead",
  },
  {
    slug: "social-media-marketing", icon: ThumbsUp, title: "Social Media Marketing",
    tagline: "Grow a community, not just followers",
    description: "End-to-end social management — content, engagement and growth strategies across every channel.",
    overview: "We run your social presence like a growth engine: content calendars, on-brand creatives, community management and collaborations that turn casual followers into loyal customers and advocates.",
    heroImage: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    included: ["Content strategy & calendar", "Creative & caption production", "Community management", "Influencer collaborations", "Growth & engagement reporting"],
    result: "Engaged community that buys",
  },
  {
    slug: "lead-generation", icon: TrendingUp, title: "Lead Generation",
    tagline: "A predictable pipeline of prospects",
    description: "Landing pages, funnels and campaigns built to capture high-intent leads and fill your sales pipeline.",
    overview: "We design lead machines end to end — high-converting landing pages, qualifying funnels and multi-channel campaigns that bring in prospects ready to buy. Every lead is tracked from click to closure so you know exactly what's working.",
    heroImage: "https://images.unsplash.com/photo-1591696205602-2f950c417cb9?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    included: ["High-converting landing pages", "Lead-qualifying funnels", "Multi-channel lead campaigns", "CRM & form integrations", "Click-to-closure tracking"],
    result: "A steady, qualified sales pipeline",
  },
];

// ─── IT Services (IT Services ▾) ───────────────────────────────────
export const IT_SERVICES = [
  {
    slug: "game-design", icon: Gamepad2, title: "Game Design",
    tagline: "Worlds worth playing in",
    description: "Concept, art and mechanics for engaging 2D and 3D games across mobile and web platforms.",
    overview: "From core loops to character art, we design games that keep players coming back. Our team handles concept, level design, UI and playable prototypes — turning a spark of an idea into a polished, addictive experience.",
    heroImage: "https://images.unsplash.com/photo-1567027757540-7b572280fa22?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    included: ["Concept & gameplay design", "Character & environment art", "Level & UI design", "Playable prototyping", "Cross-platform builds"],
    result: "Engaging, retention-driven games",
  },
  {
    slug: "annual-maintenance", icon: Wrench, title: "Annual Maintenance",
    tagline: "Always up, always updated",
    description: "Ongoing care plans that keep your website and apps secure, fast and running flawlessly all year.",
    overview: "Software needs upkeep. Our annual maintenance contracts cover updates, security patches, backups, uptime monitoring and priority support — so your digital assets stay healthy while you focus on the business.",
    heroImage: "https://images.unsplash.com/photo-1506399309177-3b43e99fead2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    included: ["Regular updates & patches", "Security hardening & backups", "Uptime & performance monitoring", "Bug fixes & small changes", "Priority WhatsApp support"],
    result: "Peace of mind, zero downtime",
  },
  {
    slug: "domain-and-hosting", icon: Server, title: "Domain & Hosting",
    tagline: "A rock-solid foundation online",
    description: "Reliable domains, fast managed hosting and SSL setup — the infrastructure your brand runs on.",
    overview: "We handle everything behind the scenes: domain registration, blazing-fast managed hosting, SSL certificates, email setup and DNS. Fast, secure and fully managed so your site is always online and quick to load.",
    heroImage: "https://images.unsplash.com/photo-1762163516269-3c143e04175c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    included: ["Domain registration & DNS", "Managed high-speed hosting", "SSL certificate setup", "Business email configuration", "Backups & uptime monitoring"],
    result: "Fast, secure, always-on hosting",
  },
  {
    slug: "database-management", icon: Database, title: "Database Management",
    tagline: "Your data, structured & safe",
    description: "Design, optimisation and administration of databases that stay fast, reliable and secure at scale.",
    overview: "Well-managed data is a competitive advantage. We design schemas, optimise queries, set up backups and monitor performance — keeping your databases fast and secure as your business grows.",
    heroImage: "https://images.unsplash.com/photo-1782094673136-5198a372980c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    included: ["Schema design & modelling", "Query & performance tuning", "Backups & disaster recovery", "Security & access control", "Scaling & migration support"],
    result: "Fast, reliable data at any scale",
  },
  {
    slug: "api-integration", icon: Plug, title: "API Integration",
    tagline: "Connect everything, seamlessly",
    description: "Integrate payment gateways, CRMs, logistics and third-party tools into one smooth workflow.",
    overview: "We connect your systems so data flows automatically — payment gateways, CRMs, shipping, WhatsApp, accounting and more. Fewer manual steps, fewer errors and a tech stack that finally talks to itself.",
    heroImage: "https://images.unsplash.com/photo-1461749280684-dccba630e2f6?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    included: ["Payment gateway integration", "CRM & ERP connections", "Logistics & shipping APIs", "Webhooks & automation", "Secure authentication setup"],
    result: "A connected, automated workflow",
  },
  {
    slug: "mobile-app-development-ios-and-android", icon: Smartphone, title: "Mobile App Development",
    tagline: "Apps for iOS & Android",
    description: "Native and cross-platform mobile apps that are fast, beautiful and built to scale with your business.",
    overview: "From idea to app store, we design and build mobile apps for iOS and Android. Clean UX, solid architecture and smooth performance — whether it's a customer app, a booking platform or an internal tool.",
    heroImage: "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    included: ["UX/UI design & prototyping", "iOS & Android development", "Cross-platform builds", "App Store & Play Store launch", "Post-launch support & updates"],
    result: "Store-ready apps that scale",
  },
  {
    slug: "software-development", icon: Code2, title: "Software Development",
    tagline: "Custom software, built right",
    description: "Bespoke web applications, dashboards and internal tools engineered around how you actually work.",
    overview: "Off-the-shelf doesn't always fit. We build custom software — dashboards, portals, automation tools and SaaS products — with clean code, secure architecture and a UX your team will actually enjoy using.",
    heroImage: "https://images.unsplash.com/photo-1542831371-29b0f74f9713?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    included: ["Requirement & architecture planning", "Custom web-app development", "Dashboards & admin panels", "Third-party integrations", "Testing, deployment & support"],
    result: "Software that fits your workflow",
  },
  {
    slug: "wordpress-development", icon: LayoutTemplate, title: "WordPress Development",
    tagline: "Powerful, editable websites",
    description: "Custom WordPress builds and themes that are fast, SEO-friendly and easy for your team to update.",
    overview: "We build WordPress sites that look custom and run fast — bespoke themes, plugin configuration, WooCommerce and a clean editing experience so your team can manage content without touching code.",
    heroImage: "https://images.unsplash.com/photo-1487338875411-8880f74114a2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    included: ["Custom theme development", "Plugin setup & configuration", "WooCommerce integration", "Speed & SEO optimisation", "Training & handover"],
    result: "A site your team can own",
  },
  {
    slug: "e-commerce-development", icon: Store, title: "E-Commerce Development",
    tagline: "Online stores that sell",
    description: "Conversion-optimised online stores on Shopify, WooCommerce or custom platforms — built to grow.",
    overview: "We build e-commerce stores designed around the buying journey — fast catalogues, frictionless checkout, payment and shipping integrations and a backend that's simple to run. Ready to scale from first sale to thousands.",
    heroImage: "https://images.unsplash.com/photo-1688561808434-886a6dd97b8c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    included: ["Store design & product setup", "Secure checkout & payments", "Shipping & tax configuration", "Inventory & order management", "Conversion-focused UX"],
    result: "A store built to convert & scale",
  },
  {
    slug: "website-development", icon: Globe, title: "Website Development",
    tagline: "Websites that build trust instantly",
    description: "Sleek, responsive, conversion-focused websites tailored to your brand and built for speed.",
    overview: "Your website is your hardest-working salesperson. We design and build fast, mobile-first sites with clear calls to action, brand-aligned visuals and clean code — turning first-time visitors into confident customers.",
    heroImage: "https://images.unsplash.com/photo-1547658719-da2b51169166?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    included: ["Responsive, mobile-first design", "Conversion-focused layouts", "Brand-aligned visual identity", "Speed & SEO best practices", "CMS & easy content editing"],
    result: "More enquiries from every visit",
  },
];


export const TEAM = [
  {
    name: "Deepali Verma",
    role: "Chief Financial Officer",
    bio: "Keeps Levaze financially sharp — steering budgets, forecasting and the numbers that let bold ideas ship on time.",
    photo: "https://customer-assets-wrfwihn1.emergentagent.net/job_levaze-immersive/artifacts/46ctt773_WhatsApp%20Image%202026-08-28%20at%204.59.54%20PM.jpeg", // Deepali Verma
    linkedin: "https://linkedin.com", twitter: "https://x.com",
  },
  {
    name: "Adarsh Verma",
    role: "Chief Marketing Officer",
    bio: "Architect of our growth playbooks. Turns brand stories into campaigns that reach the right people and convert.",
    photo: "https://customer-assets-wrfwihn1.emergentagent.net/job_levaze-immersive/artifacts/sbmkk1ar_Untitled%20design%20%283%29.png", // Adarsh Verma
    linkedin: "https://linkedin.com", twitter: "https://x.com",
  },
  {
    name: "Sneha Chaudhary",
    role: "Marketing & Operations Manager",
    bio: "The engine room of every project — keeping campaigns, clients and timelines running like clockwork.",
    photo: "https://customer-assets-wrfwihn1.emergentagent.net/job_levaze-immersive/artifacts/my2udni5_Untitled%20design.png", // Sneha Chaudhary
    linkedin: "https://linkedin.com", twitter: "https://x.com",
  },
  {
    name: "Anushree",
    role: "Animation & SEO Analyst",
    bio: "Blends motion design with search savvy — creating visuals that move people and content that ranks.",
    photo: "https://customer-assets-wrfwihn1.emergentagent.net/job_levaze-immersive/artifacts/xvso6lz6_Untitled%20design%20%282%29.png", // Anushree
    linkedin: "https://linkedin.com", twitter: "https://x.com",
  },
  {
    name: "Harshdeep",
    role: "Graphic Head",
    bio: "Leads the creative bench — shaping brand identities and visuals that make first impressions unforgettable.",
    photo: "https://customer-assets-wrfwihn1.emergentagent.net/job_levaze-immersive/artifacts/0yplct4r_WhatsApp%20Image%202026-08-28%20at%204.58.47%20PM.jpeg", // Harshdeep
    linkedin: "https://linkedin.com", twitter: "https://x.com",
  },
  {
    name: "Ritika",
    role: "Social Media Manager",
    bio: "The voice of our clients' brands online — crafting content and communities that people love to follow.",
    photo: "https://customer-assets-wrfwihn1.emergentagent.net/job_levaze-immersive/artifacts/uk3zsn2f_Untitled%20design%20%281%29.png", // Ritika
    linkedin: "https://linkedin.com", twitter: "https://x.com",
  },
];

export const LEADER = {
  name: "Mushahid Hashmi",
  role: "CEO & Founder",
  bio: "The visionary behind Levaze. Mushahid founded the agency to help small and medium businesses grow online with clarity, craft and honest partnership — turning ambitious ideas into digital-first brands across India.",
  photo: "https://customer-assets-wrfwihn1.emergentagent.net/job_levaze-immersive/artifacts/abzn1v5v_WhatsApp%20Image%202026-08-28%20at%204.47.04%20PM.jpeg", // Mr. Mushahid Hashmi
  linkedin: "https://linkedin.com", twitter: "https://x.com",
};

export const TESTIMONIALS = [
  {
    quote: "Levaze transformed our online presence! Enquiries tripled within two months of the new website going live.",
    name: "Ravi K.",
    role: "Owner, Kanchan Interiors",
    photo: "https://images.unsplash.com/photo-1521119989659-a83eee488004?auto=format&fit=crop&w=96&h=96",
  },
  {
    quote: "The team exceeded expectations at every step. Clear reporting, honest advice and real, measurable growth.",
    name: "Brian Moten",
    role: "Director, Moten Exports",
    photo: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&w=96&h=96",
  },
  {
    quote: "From SEO to social media, everything is handled by one dedicated team. Best decision we made this year.",
    name: "Clarice Turner",
    role: "Founder, Turner Boutique",
    photo: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=96&h=96",
  },
];

export const PROJECTS = [
  {
    slug: "kanchan-interiors",
    client: "Kanchan Interiors",
    industry: "Interior Design · Mumbai",
    service: "Web Design + SEO",
    image: "https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?auto=format&fit=crop&w=900&q=80",
    summary: "A dated brochure site was replaced with a portfolio-first website and local SEO push targeting Mumbai suburbs.",
    duration: "4 months",
    challenge: "Kanchan Interiors had a decade of stunning projects — and a website that showed none of them well. Photos were compressed, the site took nearly 7 seconds to load on mobile, and the studio was invisible on Google beyond its own brand name. Nearly all new business came from word of mouth, which capped growth.",
    approach: [
      "Rebuilt the website as a portfolio-first experience with full-bleed project photography and room-by-room case galleries",
      "Compressed and lazy-loaded all media, cutting load time from 6.8s to 1.4s",
      "Ran a local SEO campaign targeting 'interior designer + suburb' keywords across Mumbai",
      "Added WhatsApp and consultation-booking CTAs on every project page",
    ],
    outcome: "Within four months the studio ranked #2 for its main keyword and enquiries nearly quadrupled. More importantly, clients now arrive pre-sold — they've already seen the portfolio before the first call.",
    quote: { text: "Levaze transformed our online presence! Enquiries tripled within two months of the new website going live.", author: "Ravi K., Owner" },
    gallery: [
      "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?auto=format&fit=crop&w=800&q=80",
      "https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?auto=format&fit=crop&w=800&q=80",
      "https://images.unsplash.com/photo-1615874959474-d609969a20ed?auto=format&fit=crop&w=800&q=80",
    ],
    results: [
      { metric: "Monthly enquiries", before: "8", after: "31" },
      { metric: "Google ranking (main keyword)", before: "Page 4", after: "#2" },
      { metric: "Site load time", before: "6.8s", after: "1.4s" },
    ],
  },
  {
    slug: "turner-boutique",
    client: "Turner Boutique",
    industry: "Fashion Retail · Pune",
    service: "Social Media + Marketing",
    image: "https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&w=900&q=80",
    summary: "Instagram-first content strategy with reels, influencer tie-ups and retargeted Meta ads for festive collections.",
    duration: "6 months",
    challenge: "A beautiful boutique with loyal walk-in customers — but online, Turner Boutique was posting sporadic product photos to 2,000 followers with almost no engagement. Festive collections sold out in-store but online orders barely moved.",
    approach: [
      "Built a reels-first content calendar: styling videos, behind-the-scenes and festive lookbooks",
      "Partnered with five Pune-based micro-influencers for collection launches",
      "Set up Meta retargeting funnels for cart abandoners and profile visitors",
      "Synced online drops with in-store festive events to compound buzz",
    ],
    outcome: "Six months in, the account grew 9x and online orders became a serious revenue line — 215 orders a month at 4.6x return on ad spend during the festive quarter.",
    quote: { text: "The team exceeded expectations at every step. Clear reporting, honest advice and real, measurable growth.", author: "Clarice Turner, Founder" },
    gallery: [
      "https://images.unsplash.com/photo-1445205170230-053b83016050?auto=format&fit=crop&w=800&q=80",
      "https://images.unsplash.com/photo-1483985988355-763728e1935b?auto=format&fit=crop&w=800&q=80",
      "https://images.unsplash.com/photo-1490481651871-ab68de25d43d?auto=format&fit=crop&w=800&q=80",
    ],
    results: [
      { metric: "Instagram followers", before: "2.1k", after: "18.4k" },
      { metric: "Monthly online orders", before: "40", after: "215" },
      { metric: "Return on ad spend", before: "1.2x", after: "4.6x" },
    ],
  },
  {
    slug: "moten-exports",
    client: "Moten Exports",
    industry: "Manufacturing · Noida",
    service: "Web Design + Analytics",
    image: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&w=900&q=80",
    summary: "B2B catalogue website with enquiry funnels, GA4 tracking and quarterly dashboards for the sales team.",
    duration: "3 months",
    challenge: "Moten Exports relied on trade fairs and a PDF catalogue emailed on request. International buyers who found the old website bounced within seconds — no product specs, no enquiry flow, no trust signals. The sales team had zero visibility into who was visiting.",
    approach: [
      "Designed a structured B2B catalogue with spec sheets, certifications and MOQ details per product line",
      "Built enquiry funnels with smart forms that route leads to the right sales rep",
      "Implemented GA4 with conversion tracking on every enquiry touchpoint",
      "Delivered quarterly dashboards showing lead source, geography and product interest",
    ],
    outcome: "Qualified leads quadrupled in a single quarter and the sales team now walks into calls knowing exactly which products a buyer browsed. Quote-to-close rate more than doubled.",
    quote: { text: "For the first time we can see our pipeline clearly. The dashboards alone are worth it.", author: "Brian Moten, Director" },
    gallery: [
      "https://images.unsplash.com/photo-1565043666747-69f6646db940?auto=format&fit=crop&w=800&q=80",
      "https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?auto=format&fit=crop&w=800&q=80",
      "https://images.unsplash.com/photo-1531973576160-7125cd663d86?auto=format&fit=crop&w=800&q=80",
    ],
    results: [
      { metric: "Qualified B2B leads / month", before: "5", after: "22" },
      { metric: "Bounce rate", before: "74%", after: "38%" },
      { metric: "Quote-to-close rate", before: "9%", after: "21%" },
    ],
  },
  {
    slug: "spice-route-kitchen",
    client: "Spice Route Kitchen",
    industry: "Restaurant · Haridwar",
    service: "SEO + Social Media",
    image: "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&w=900&q=80",
    summary: "Google Business optimisation, review strategy and weekend-offer campaigns for a family restaurant.",
    duration: "5 months",
    challenge: "A beloved family restaurant that tourists simply couldn't find. The Google Business profile had outdated hours, twelve photos and a 3.8 rating dragged down by two old reviews. Weekends were busy; weekdays were empty.",
    approach: [
      "Fully rebuilt the Google Business profile: menus, 60+ dish photos, accurate hours and attributes",
      "Launched a table-side review prompt that lifted the rating to 4.7 in three months",
      "Ran geo-targeted weekend-offer campaigns aimed at tourists within 5 km",
      "Added a simple web booking flow connected to WhatsApp confirmations",
    ],
    outcome: "Maps views grew 7x and weekday covers rose steadily as tourists began discovering the restaurant. Online table bookings went from a dozen to nearly a hundred a month.",
    quote: { text: "Tourists now tell us they found us on Google. That never happened before Levaze.", author: "Owner, Spice Route Kitchen" },
    gallery: [
      "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?auto=format&fit=crop&w=800&q=80",
      "https://images.unsplash.com/photo-1555396273-367ea4eb4db5?auto=format&fit=crop&w=800&q=80",
      "https://images.unsplash.com/photo-1552566626-52f8b828add9?auto=format&fit=crop&w=800&q=80",
    ],
    results: [
      { metric: "Google Maps views / month", before: "1.9k", after: "14.2k" },
      { metric: "Table bookings via web", before: "12", after: "96" },
      { metric: "Average rating", before: "3.8★", after: "4.7★" },
    ],
  },
  {
    slug: "careplus-clinic",
    client: "CarePlus Clinic",
    industry: "Healthcare · Mira Road",
    service: "Web Design + Marketing",
    image: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?auto=format&fit=crop&w=900&q=80",
    summary: "Appointment-booking website with WhatsApp integration and locality-targeted Google ads.",
    duration: "4 months",
    challenge: "CarePlus ran entirely on phone bookings — a receptionist juggling calls while patients waited. New residents in the area defaulted to bigger hospital chains simply because the clinic had no online presence to compete with.",
    approach: [
      "Built an appointment-booking website with doctor profiles, timings and specialities",
      "Integrated WhatsApp confirmations and automated reminder messages",
      "Ran locality-targeted Google ads for high-intent searches like 'paediatrician near me'",
      "Set up review collection after visits to build local trust signals",
    ],
    outcome: "From zero online bookings to 140+ a month, at less than a third of the previous patient acquisition cost. Automated reminders cut no-shows by two-thirds.",
    quote: { text: "Our receptionist finally has breathing room, and patients love the WhatsApp reminders.", author: "Dr. Mehta, CarePlus Clinic" },
    gallery: [
      "https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?auto=format&fit=crop&w=800&q=80",
      "https://images.unsplash.com/photo-1579684385127-1ef15d508118?auto=format&fit=crop&w=800&q=80",
      "https://images.unsplash.com/photo-1538108149393-fbbd81895907?auto=format&fit=crop&w=800&q=80",
    ],
    results: [
      { metric: "Online appointments / month", before: "0", after: "140+" },
      { metric: "Cost per new patient", before: "₹610", after: "₹185" },
      { metric: "No-show rate", before: "32%", after: "11%" },
    ],
  },
  {
    slug: "shivalik-estates",
    client: "Shivalik Estates",
    industry: "Real Estate · Dehradun",
    service: "Marketing + Analytics",
    image: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=900&q=80",
    summary: "Lead-generation funnels for two residential projects with virtual site-tour landing pages.",
    duration: "6 months",
    challenge: "Two residential projects, one overworked sales team, and ad money burning on portals that delivered window-shoppers. Site visits were expensive to arrange and most leads were never serious buyers.",
    approach: [
      "Built dedicated landing pages per project with virtual site tours and floor-plan downloads",
      "Created lead-scoring funnels that qualify budget and timeline before a site visit is booked",
      "Shifted spend from property portals to targeted Google and Meta campaigns",
      "Tracked every lead from click to closure with a custom analytics dashboard",
    ],
    outcome: "Cost per qualified lead dropped by nearly two-thirds while site-visit bookings almost quintupled. Nineteen units sold through the digital funnel in six months — up from three.",
    quote: { text: "The virtual tours filter out casual browsers. Everyone who visits now is genuinely interested.", author: "Sales Head, Shivalik Estates" },
    gallery: [
      "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&w=800&q=80",
      "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=800&q=80",
      "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&w=800&q=80",
    ],
    results: [
      { metric: "Site-visit bookings / month", before: "14", after: "67" },
      { metric: "Cost per qualified lead", before: "₹1,450", after: "₹520" },
      { metric: "Units sold via digital", before: "3", after: "19" },
    ],
  },
];

export const STATS = [
  { value: "120+", label: "Projects Delivered" },
  { value: "85+", label: "Happy Clients" },
  { value: "3x", label: "Avg. Lead Growth" },
  { value: "5+", label: "Years of Craft" },
];
